import { useTranslation } from "react-i18next";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import BaseDivider from "@/components_v2/Divider/BaseDivider";
import BaseIconButton from "@/components_v2/Button/BaseIconButton";
import SidebarMenu from "@/components_v2/Menu/SidebarMenu";
import PrmeLogo from "@/assets/icons/prme-logo.png";
import PrmeLogoCollapsed from "@/assets/icons/prme-site-icon.png";
import NotificationsPopover from "@/features/notifications/popover";
import NotificationsSidebarTrigger from "./Notifications/NotificationsSidebarTrigger";
import AccountPopover from "@/features/account/popover";
import AccountSidebarTrigger from "./Account/AccountSidebarTrigger";
import useMenuItems from "./useMenuItems";
import type { SidebarContentProps } from "./types";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "./styles.css";

const SidebarContent = ({
  currentUser,
  isCollapsed,
  showCollapseTrigger = false,
  showLogo = true,
  showFooterActions = true,
  onToggleCollapsed,
  className,
}: SidebarContentProps) => {
  const { t } = useTranslation();
  const menuItems = useMenuItems(currentUser);

  return (
    <>
      {showCollapseTrigger && onToggleCollapsed ? (
        <BaseIconButton
          variant={BUTTON_VARIANTS.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          shape="circle"
          className="side-menu-collapse-trigger"
          icon={isCollapsed ? <RightOutlined /> : <LeftOutlined />}
          onClick={onToggleCollapsed}
          ariaLabel={
            isCollapsed
              ? t("layouts.labels.expand")
              : t("layouts.labels.collapse")
          }
        />
      ) : null}

      <div className={`flex flex-col full-height ${className ?? ""}`}>
        <div className="flex flex-col gap-400 flex-1">
          {showLogo ? (
            <div className="side-menu-logo-container self-center">
              <img
                className="side-menu-logo"
                src={PrmeLogo}
                alt={t("layouts.labels.logo")}
              />

              <img
                className="side-menu-logo-collapsed"
                src={PrmeLogoCollapsed}
                alt={t("layouts.labels.logo")}
              />
            </div>
          ) : null}

          <SidebarMenu items={menuItems} isCollapsed={isCollapsed} />
        </div>

        {showFooterActions ? (
          <div className="flex flex-col flex-shrink-0">
            <BaseDivider />

            <NotificationsPopover>
              {({ isOpen, unreadCount }) => (
                <NotificationsSidebarTrigger
                  isCollapsed={isCollapsed}
                  isOpen={isOpen}
                  unreadCount={unreadCount}
                />
              )}
            </NotificationsPopover>

            <AccountPopover currentUser={currentUser}>
              {({ isOpen }) => (
                <AccountSidebarTrigger
                  currentUser={currentUser}
                  isCollapsed={isCollapsed}
                  isOpen={isOpen}
                />
              )}
            </AccountPopover>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default SidebarContent;
